import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BlogSection from '../components/BlogSection';
import HabitsTypewriterSection from '../components/HabitsTypewriterSection';
import SeoMeta from '../components/SeoMeta';
import SoftwareApplicationJsonLd from '../components/SoftwareApplicationJsonLd';
import EmailCaptureSection from '../components/EmailCaptureSection';
import FaqSection from '../components/FaqSection';
import { SITE_NAME } from '../config/site';
import {
  buildFaqSchema,
  buildHowToSchema,
  buildOrganizationSchema,
  buildWebSiteSchema,
} from '../data/structuredData';
import { useLeadModal } from '../context/LeadModalContext';

const homeTitle = `${SITE_NAME} | Weight Loss Without Calorie Counting—Simple Diet Plan India`;
const homeDescription =
  'Notrack is a no tracking diet app for India. Get weight loss without calorie counting, simple Indian meals, daily guidance, and early access pricing. Join 500+ on the waitlist.';

export default function HomePage() {
  const { openModal } = useLeadModal();
  const { hash } = useLocation();
  const [isMobileHero, setIsMobileHero] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobileHero(Boolean(mq.matches));
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  const heroBgStyle = useMemo(() => {
    const img = isMobileHero ? '/verticalbg.jpg' : '/horizontalbg.jpg';
    return { backgroundImage: `url(${process.env.PUBLIC_URL}${img})` };
  }, [isMobileHero]);

  const personalizationBgStyle = useMemo(() => {
    const img = isMobileHero ? '/verticalbg.jpg' : '/horizontalbg.jpg';
    return { backgroundImage: `url(${process.env.PUBLIC_URL}${img})` };
  }, [isMobileHero]);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [hash]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const candidates = document.querySelectorAll(
      '.section, .card, .grid-card, .pricing-card, .faq-section__list, .how-step, .trust-card'
    );
    /** Skip #hero: on small viewports it is reordered above the fold; hiding .card/.grid-card there reads as a blank page. */
    const animated = [...candidates].filter((el) => !el.closest('.hero'));

    animated.forEach((el) => el.classList.add('anim-init'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('anim-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px 0px 0px' }
    );

    animated.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const faqLd = buildFaqSchema();
  const howToLd = buildHowToSchema();
  const webLd = buildWebSiteSchema();
  const orgLd = buildOrganizationSchema();

  return (
    <>
      <SeoMeta title={homeTitle} description={homeDescription} path="/" ogType="website">
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(howToLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webLd)}</script>
        <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      </SeoMeta>
      <SoftwareApplicationJsonLd />

      <main>
        <section className="hero" id="hero" aria-label="Hero">
          <div className="hero-bg" style={heroBgStyle} aria-hidden />
          <div className="hero-ambient" aria-hidden>
            <div className="hero-ambient__blob hero-ambient__blob--a" />
            <div className="hero-ambient__blob hero-ambient__blob--b" />
            <div className="hero-ambient__ring" />
          </div>
          <div className="container hero-inner">
            <div className="hero-copy">
              <p className="hero-eyebrow">
                <span className="hero-eyebrow__dot" aria-hidden />
                Built for Indian food. Powered by smart guidance.
              </p>
              <h1 className="hero-headline">
                Lose weight <span className="hero-headline__muted">without</span>{' '}
                <span className="hero-headline__accent">tracking calories</span>
              </h1>
              <p className="hero-dek">
                An AI-powered system that guides your meals, adapts to your progress, and keeps you consistent—without logging
                every bite.
              </p>
              <ul className="hero-chips" aria-label="Product highlights">
                <li>No calorie tracking</li>
                <li>Works with home-cooked Indian meals</li>
                <li>AI adapts to your progress</li>
              </ul>
              <div className="hero-promo" role="note" aria-label="Offer highlight">
                <span className="hero-promo__icon" aria-hidden>
                  ✦
                </span>
                <span>Get 2 months for the price of 1 — early users only</span>
              </div>
              <div className="hero-cta-row">
                <button className="btn btn-primary" type="button" onClick={() => openModal('Founding Plan')}>
                  👉 Lock Early Access
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => scrollTo('how')}>
                  See how it works
                </button>
              </div>
              <div className="under-cta">
                <div className="under-cta-line">No credit card required</div>
                <div className="under-cta-line">Join 500+ people on the waitlist</div>
              </div>
            </div>

            <div className="hero-showcase">
              <div className="hero-bento">
                <article className="hero-glass hero-glass--main" aria-label="Sample day on Notrack">
                  <div className="hero-glass__head">
                    <div className="hero-glass__head-start">
                      <span className="badge">Today</span>
                      <span className="hero-glass__live" aria-hidden>
                        <span className="hero-glass__live-dot" />
                        Live preview
                      </span>
                    </div>
                    <div className="signal" aria-hidden>
                      <span className="signal-dot" />
                      <span className="signal-dot" />
                      <span className="signal-dot" />
                    </div>
                  </div>
                  <div className="hero-glass__body">
                    <p className="hero-glass__lede">A plan you can actually follow</p>
                    <div className="hero-meals">
                      <div className="hero-meal">
                        <div className="hero-meal-label">Breakfast</div>
                        <div className="hero-meal-value">Eggs / poha / idli</div>
                      </div>
                      <div className="hero-meal">
                        <div className="hero-meal-label">Lunch</div>
                        <div className="hero-meal-value">Dal + rice + sabzi</div>
                      </div>
                      <div className="hero-meal">
                        <div className="hero-meal-label">Dinner</div>
                        <div className="hero-meal-value">Roti + chicken/paneer</div>
                      </div>
                    </div>
                    <p className="hero-glass__foot">No logging. No tracking. Just follow.</p>
                  </div>
                </article>
                <div className="hero-stat-stack">
                  <div className="hero-stat-tile">
                    <div className="metric-kicker">Built for</div>
                    <div className="metric-value">Real life</div>
                  </div>
                  <div className="hero-stat-tile">
                    <div className="metric-kicker">Focus</div>
                    <div className="metric-value">Consistency</div>
                  </div>
                  <div className="hero-stat-tile">
                    <div className="metric-kicker">Method</div>
                    <div className="metric-value">Simple meals</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="hero-below" aria-label="Personalization highlight">
            <div className="container">
              <div className="hero-below__card" role="note" aria-label="Personalized highlight">
                <span className="hero-below__icon" aria-hidden>
                  ✦
                </span>
                <div className="hero-below__copy">
                  <p className="hero-below__line">Personalized to your goal, your meals, and your progress</p>
                  <p className="hero-below__sub">Adaptive guidance • Works with Indian home food • No calorie logging</p>
                </div>
              </div>
            </div>
          </section>
        </section>

        <section className="positioning-strip" aria-label="Positioning">
          <div className="positioning-strip__bg" aria-hidden />
          <div className="positioning-strip__shell">
            <p className="positioning-strip__line">You don’t need more diet information.</p>
            <p className="positioning-strip__line positioning-strip__line--accent">
              You need a system that thinks with you.
            </p>
          </div>
        </section>

        <section
          className="section real-life-section"
          id="real-life"
          aria-labelledby="real-life-title"
        >
          <div className="real-life-section__bg" aria-hidden />
          <div className="real-life-section__ambient" aria-hidden>
            <div className="real-life-section__blob real-life-section__blob--a" />
            <div className="real-life-section__blob real-life-section__blob--b" />
          </div>
          <div className="real-life-section__shell">
            <header className="real-life-section__masthead">
              <p className="real-life-section__kicker">
                <span className="real-life-section__kicker-dot" aria-hidden />
                Real life, not theory
              </p>
              <h2 id="real-life-title" className="real-life-section__headline">
                Built from real experience,{' '}
                <span className="real-life-section__headline-accent">not diet theory</span>
              </h2>
              <div className="card real-life-section__open real-life-section__glass">
                <p className="real-life-section__lede">Most diet apps are built in labs.</p>
                <p className="real-life-section__lede real-life-section__lede--emph">
                  {SITE_NAME} is built from real life.
                </p>
              </div>
            </header>

            <div className="real-life-section__stage">
              <div className="card real-life-section__panel real-life-section__panel--pain">
                <h3 className="real-life-section__h3">If you’ve tried dieting, you’ve probably felt this</h3>
                <ul className="real-life-section__list real-life-section__list--pain">
                  <li>You can’t carry a weighing scale everywhere</li>
                  <li>You don’t know how much your home food actually weighs</li>
                  <li>You stop eating your mom’s food—and feel bad about it</li>
                  <li>You constantly stress about calories</li>
                  <li>You don’t know what to eat outside</li>
                  <li>Diets become expensive and confusing</li>
                </ul>
                <p className="real-life-section__punch">
                  <span className="real-life-section__finger" aria-hidden>
                    👉
                  </span>{' '}
                  And eventually… you quit.
                </p>
              </div>

              <div className="real-life-section__stack">
                <div className="card real-life-section__panel real-life-section__panel--shift">
                  <h3 className="real-life-section__h3">So We tried something different</h3>
                  <p className="real-life-section__body">
                    Instead of tracking everything, I focused on simple habits.
                  </p>
                </div>
                <div className="card real-life-section__panel real-life-section__panel--worked">
                  <h3 className="real-life-section__h3 real-life-section__h3--worked">
                    What actually works (and what this system is built on)
                  </h3>
                  <ul className="real-life-section__list real-life-section__list--worked real-life-section__list--columns">
                    <li>Sleep 7–8 hours</li>
                    <li>Reduce sugar intake</li>
                    <li>Eat simple home food</li>
                    <li>Balance portions</li>
                    <li>Include protein</li>
                    <li>Track progress, not calories</li>
                    <li>Stay active</li>
                    <li>Drink more water</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card real-life-section__insight">
              <p className="real-life-section__insight-punch">
                <span className="real-life-section__finger" aria-hidden>
                  👉
                </span>{' '}
                These are the patterns the system reinforces.
              </p>
            </div>

            <div className="real-life-section__footer">
              <div className="card real-life-section__panel real-life-section__panel--close">
                <h3 className="real-life-section__h3">This is what {SITE_NAME} is built on</h3>
                <p className="real-life-section__body">A system based on what actually works in real life.</p>
                <p className="real-life-section__body real-life-section__body--muted">
                  Not perfection.
                  <br />
                  Not strict rules.
                </p>
                <p className="real-life-section__close-punch">
                  <span className="real-life-section__finger" aria-hidden>
                    👉
                  </span>{' '}
                  Just simple habits you can follow every day.
                </p>
              </div>
              <div className="real-life-section__cta">
                <button type="button" className="btn btn-primary" onClick={() => openModal('Start without tracking')}>
                  👉 Start your journey without tracking
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="section about-india" id="what-is-notrack" aria-label="About Notrack for SEO">
          <div className="about-india__glow" aria-hidden />
          <div className="container about-india__wrap">
            <header className="about-india__intro">
              <p className="about-india__eyebrow">Built for Indian kitchens</p>
              <h2 className="about-india__title">
                Weight loss without calorie counting
                <span className="about-india__title-line">—built for India</span>
              </h2>
              <p className="about-india__lead">
                Readers searching for a <strong>no tracking diet</strong> or{' '}
                <strong>weight loss without calorie counting</strong> usually hit the same wall: logging breaks on busy days.{' '}
                {SITE_NAME} replaces spreadsheets with structure for roti, rice, dal, eggs, paneer, and chicken—so you can eat
                home food and stay consistent.
              </p>
            </header>
            <div className="about-india__panels">
              <article className="about-india__panel">
                <div className="about-india__panel-head">
                  <span className="about-india__panel-index" aria-hidden>
                    01
                  </span>
                  <h3 className="about-india__panel-title">Simple diet plan India (real kitchens)</h3>
                </div>
                <p className="about-india__panel-copy">
                  Whether you are in Mumbai, Bengaluru, Delhi NCR, or Hyderabad, the winning pattern is similar: enough protein,
                  vegetables you enjoy, sensible starch portions, and fewer “invisible” calories from fried snacks and sweet
                  drinks—not importing meal plans you will not cook.
                </p>
              </article>
              <article className="about-india__panel about-india__panel--accent">
                <div className="about-india__panel-head">
                  <span className="about-india__panel-index" aria-hidden>
                    02
                  </span>
                  <h3 className="about-india__panel-title">No tracking diet, yes accountability</h3>
                </div>
                <p className="about-india__panel-copy">
                  You still need clarity. {SITE_NAME} gives daily guidance and habits so you know what to do next—even when you
                  skip a day. That is how long-tail goals like sustainable fat loss actually stick past week three.
                </p>
                <button type="button" className="btn btn-secondary about-india__panel-cta" onClick={() => scrollTo('blog')}>
                  Read free guides
                </button>
              </article>
            </div>
          </div>
        </section>

        <section className="section problem-section" id="problem" aria-label="Problem">
          <div className="problem-section__mesh" aria-hidden />
          <div className="container problem-section__wrap">
            <header className="problem-section__intro">
              <p className="problem-section__eyebrow">The pattern we all know</p>
              <h2 className="problem-section__title">Why diet apps fail in India</h2>
              <p className="problem-section__lead">
                Tracking breaks in real kitchens.
              </p>
            </header>

            <div className="problem-section__grid">
              <div className="problem-section__pain">
                <h3 className="problem-section__pain-label">What makes Indian food hard to track</h3>
                <ul className="problem-section__spiral" aria-label="Why tracking breaks">
                  <li>Indian food is hard to track</li>
                  <li>Recipes change every day</li>
                  <li>Ingredients aren’t measured</li>
                </ul>
                <p className="problem-section__aside" style={{ marginTop: '1rem' }}>
                  You try to log everything… You guess… You miss a few days…
                </p>
                <ol className="problem-section__spiral" aria-label="What often happens next">
                  <li>👉 And you quit.</li>
                </ol>
              </div>

              <aside className="problem-section__relief" aria-label="Notrack’s approach">
                <blockquote className="problem-section__quote">
                  You don’t need tracking. You need guidance that adapts.
                </blockquote>
                <p className="problem-section__relief-copy">
                  {SITE_NAME} helps you stay consistent with smart daily direction—without logging every bite.
                </p>
                <button className="btn btn-primary" type="button" onClick={() => scrollTo('solution')}>
                  See how it works
                </button>
              </aside>
            </div>
          </div>
        </section>

        <section className="section solution-section" id="solution" aria-label="Solution">
          <div className="solution-section__glow" aria-hidden />
          <div className="container solution-section__wrap">
            <header className="solution-section__intro">
              <p className="solution-section__eyebrow">The Notrack way</p>
              <h2 className="solution-section__title">Notrack replaces tracking with intelligent guidance</h2>
              <p className="solution-section__lead">Instead of tracking everything, Notrack uses AI to:</p>
            </header>

            <ul className="solution-section__pillars" role="list">
              <li>
                <article className="solution-section__card">
                  <div className="solution-section__mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="solution-section__card-title">Understand your eating patterns</h3>
                  <p className="solution-section__card-copy">So guidance feels natural, not strict.</p>
                </article>
              </li>
              <li>
                <article className="solution-section__card">
                  <div className="solution-section__mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="solution-section__card-title">Guide your daily meals</h3>
                  <p className="solution-section__card-copy">Clear direction without calorie math.</p>
                </article>
              </li>
              <li>
                <article className="solution-section__card">
                  <div className="solution-section__mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="solution-section__card-title">Adjust your plan based on progress</h3>
                  <p className="solution-section__card-copy">Based on your consistency and results.</p>
                </article>
              </li>
            </ul>

            <p className="solution-section__tagline">👉 No logging. Just direction.</p>
          </div>
        </section>

        <section className="section how-section" id="how" aria-label="How it works">
          <div className="how-section__rail" aria-hidden />
          <div className="container how-section__wrap">
            <header className="how-section__intro">
              <p className="how-section__eyebrow">Simple flow</p>
              <h2 className="how-section__title">A system that adapts to you</h2>
            </header>

            <div className="how-section__grid">
              <ol className="how-section__steps">
                <li className="how-step how-section__step">
                  <article className="how-section__card">
                    <span className="how-section__index" aria-hidden>
                      1
                    </span>
                    <h3 className="how-section__heading">Get your personalized starting plan</h3>
                    <p className="how-section__text">Based on your goal, lifestyle, and food habits</p>
                  </article>
                </li>
                <li className="how-step how-section__step">
                  <article className="how-section__card">
                    <span className="how-section__index" aria-hidden>
                      2
                    </span>
                    <h3 className="how-section__heading">Follow simple daily guidance</h3>
                    <p className="how-section__text">No calorie tracking. Just clear direction</p>
                  </article>
                </li>
                <li className="how-step how-section__step">
                  <article className="how-section__card">
                    <span className="how-section__index" aria-hidden>
                      3
                    </span>
                    <h3 className="how-section__heading">Check in daily</h3>
                    <p className="how-section__text">Takes seconds</p>
                  </article>
                </li>
                <li className="how-step how-section__step">
                  <article className="how-section__card">
                    <span className="how-section__index" aria-hidden>
                      4
                    </span>
                    <h3 className="how-section__heading">AI adjusts your plan</h3>
                    <p className="how-section__text">Based on your consistency and progress</p>
                  </article>
                </li>
              </ol>

              <aside className="how-section__proof" aria-label="App interface preview">
                <div className="how-section__mockup">
                  <img
                    src={`${process.env.PUBLIC_URL}/ui-mockup.webp`}
                    loading="lazy"
                    decoding="async"
                    alt="Notrack simple daily habit tracker mobile interface"
                    className="how-section__mockup-img"
                  />
                </div>
                <p className="how-section__proof-note">
                  A simple interface you can follow daily—no calorie logging.
                </p>
              </aside>
            </div>

            <p className="how-section__tagline">👉 You’re never guessing what to do next.</p>
          </div>
        </section>

        <section className="section personalization-section" id="personalization" aria-label="Personalization">
          <div className="personalization-section__bg" style={personalizationBgStyle} aria-hidden />
          <div className="container personalization-section__inner">
            <header className="personalization-section__intro">
              <p className="personalization-section__eyebrow">Personalized</p>
              <h2 className="personalization-section__title">Not the same plan for everyone</h2>
              <p className="personalization-section__lead">Your plan is shaped by:</p>
            </header>

            <div className="personalization-section__grid">
              <article className="card personalization-section__card">
                <h3 className="personalization-section__h3">Notrack adjusts based on:</h3>
                <ul className="personalization-section__list">
                  <li>Your progress</li>
                  <li>Your consistency</li>
                  <li>Your real-life routine</li>
                </ul>
                <p className="personalization-section__tagline">👉 The more you use it, the better it works for you.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section food-section" id="food" aria-label="Food section">
          <div className="food-section__wash" aria-hidden />
          <div className="container food-section__inner">
            <header className="food-section__intro">
              <p className="food-section__eyebrow">Your kitchen · Real meals</p>
              <h2 className="food-section__title">Built for real Indian food</h2>
              <p className="food-section__sub">Roti. Rice. Dal. Eggs. Paneer. Chicken.</p>
            </header>

            <div className="food-section__grid">
              <article className="card food-section__panel food-section__panel--list">
                <h3 className="food-section__panel-head">No diet food. No complicated recipes.</h3>
                <ul className="food-section__bullets">
                  <li>Roti</li>
                  <li>Rice</li>
                  <li>Dal</li>
                  <li>Eggs</li>
                  <li>Chicken</li>
                  <li>Paneer</li>
                </ul>
                <p className="food-section__panel-note">
                  <span className="food-section__note-icon" aria-hidden>
                    ✦
                  </span>
                  👉 Same food. Smarter structure.
                </p>
              </article>

              <article className="card food-section__panel food-section__panel--platter">
                <div className="food-section__platter-head">
                  <span className="food-section__platter-kicker">Mix & match</span>
                  <h3 className="food-section__platter-title">Examples you can rotate daily</h3>
                </div>
                <ul className="food-section__cloud" aria-label="Example foods">
                  {[
                    'Roti',
                    'Rice',
                    'Dal',
                    'Eggs',
                    'Chicken',
                    'Paneer',
                  ].map((label) => (
                    <li key={label}>
                      <span className="food-section__chip">{label}</span>
                    </li>
                  ))}
                </ul>
                <p className="food-section__platter-foot">Normal food, better structure, consistent outcomes.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section why-section" id="why" aria-label="Why Notrack works">
          <div className="why-section__wash" aria-hidden />
          <div className="container why-section__inner">
            <header className="why-section__intro">
              <p className="why-section__eyebrow">Why it sticks</p>
              <h2 className="why-section__title">Why Notrack works (when others don’t)</h2>
            </header>

            <div className="why-compare" aria-label="Comparison: Most apps vs Notrack">
              <article className="card why-compare__card why-compare__card--apps" aria-label="Most apps">
                <p className="why-compare__kicker">Most apps</p>
                <h3 className="why-compare__title">Static plans + tracking pressure</h3>
                <ul className="why-compare__list why-compare__list--apps">
                  <li>
                    <span className="why-compare__icon why-compare__icon--x" aria-hidden>
                      ✕
                    </span>
                    Track everything
                  </li>
                  <li>
                    <span className="why-compare__icon why-compare__icon--x" aria-hidden>
                      ✕
                    </span>
                    Give static plans
                  </li>
                  <li>
                    <span className="why-compare__icon why-compare__icon--x" aria-hidden>
                      ✕
                    </span>
                    Expect perfection
                  </li>
                </ul>
              </article>

              <article className="card why-compare__card why-compare__card--notrack" aria-label="Notrack">
                <p className="why-compare__kicker why-compare__kicker--accent">Notrack</p>
                <h3 className="why-compare__title">Guidance that adapts to real life</h3>
                <ul className="why-compare__list why-compare__list--notrack">
                  <li>
                    <span className="why-compare__icon why-compare__icon--check" aria-hidden>
                      ✓
                    </span>
                    No tracking required
                  </li>
                  <li>
                    <span className="why-compare__icon why-compare__icon--check" aria-hidden>
                      ✓
                    </span>
                    AI adapts to your behavior
                  </li>
                  <li>
                    <span className="why-compare__icon why-compare__icon--check" aria-hidden>
                      ✓
                    </span>
                    Built for Indian home food
                  </li>
                  <li>
                    <span className="why-compare__icon why-compare__icon--check" aria-hidden>
                      ✓
                    </span>
                    Keeps you consistent—even when you miss days
                  </li>
                </ul>
              </article>
            </div>

            <p className="why-section__tagline">
              👉 This is not a diet plan. <br />
              👉 It’s a system that evolves with you.
            </p>
          </div>
        </section>

        <HabitsTypewriterSection />

        <section className="section trust-section" id="trust" aria-labelledby="trust-heading">
          <div className="trust-section__wash" aria-hidden />
          <div className="container trust-section__inner">
            <header className="trust-section__masthead">
              <p className="trust-section__eyebrow">Everyday life, first</p>
              <h2 id="trust-heading" className="trust-section__title">
                Information is everywhere. Consistency is not.
              </h2>
              <div className="trust-section__rule" aria-hidden />

              <div className="trust-section__lede">
                <p>You already know what to eat.</p>
                <p>The challenge is staying consistent.</p>
              </div>

              <div className="trust-section__rule trust-section__rule--spaced" aria-hidden />
            </header>

            <ul className="trust-section__features" role="list">
              <li>
                <article className="card trust-card trust-section__card trust-section__card--a">
                  <div className="trust-section__card-mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="trust-section__card-title">Guiding your daily decisions</h3>
                  <p className="trust-section__card-lead">Clear next steps.</p>
                  <p className="trust-section__card-body">So you’re never guessing what to do next.</p>
                </article>
              </li>
              <li>
                <article className="card trust-card trust-section__card trust-section__card--b">
                  <div className="trust-section__card-mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="trust-section__card-title">Adjusting when you fall off</h3>
                  <p className="trust-section__card-lead">Miss days? You continue.</p>
                  <p className="trust-section__card-body">The system adapts instead of resetting you.</p>
                </article>
              </li>
              <li>
                <article className="card trust-card trust-section__card trust-section__card--c">
                  <div className="trust-section__card-mark" aria-hidden>
                    ✓
                  </div>
                  <h3 className="trust-section__card-title">Keeping you moving forward</h3>
                  <p className="trust-section__card-lead">Less guilt. More consistency.</p>
                  <p className="trust-section__card-body">Progress that fits real schedules.</p>
                </article>
              </li>
            </ul>
          </div>
        </section>

        <BlogSection />
        <EmailCaptureSection />

        <section className="section social-proof" aria-labelledby="social-proof-heading">
          <div className="social-proof__wash" aria-hidden />
          <div className="container social-proof__inner">
            <header className="social-proof__intro">
              <p className="social-proof__eyebrow">Social proof</p>
              <h2 id="social-proof-heading" className="social-proof__title">
                People want simple—not tracking
              </h2>
              <p className="social-proof__lede">What early readers and users say about the no-logging approach.</p>
            </header>

            <div className="social-proof__grid" role="list" aria-label="Testimonials">
              <article className="card social-proof__card" role="listitem">
                <p className="social-proof__quote">
                  “Notrack finally let me eat dal chawal without feeling guilty. It just works.”
                </p>
                <p className="social-proof__who">Rahul, Mumbai</p>
              </article>
              <article className="card social-proof__card" role="listitem">
                <p className="social-proof__quote">
                  “I stopped counting calories and just followed the daily structure. Lost 3kg in a month.”
                </p>
                <p className="social-proof__who">Priya, Bengaluru</p>
              </article>
              <article className="card social-proof__card" role="listitem">
                <p className="social-proof__quote">
                  “The best part is no logging. I just eat my home food and check off my habits.”
                </p>
                <p className="social-proof__who">Amit, Delhi</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section pricing-section" id="pricing" aria-labelledby="pricing-heading">
          <div className="pricing-section__wash" aria-hidden />
          <div className="container pricing-section__inner">
            <header className="pricing-section__masthead">
              <p className="pricing-section__eyebrow">Early access pricing</p>
              <h2 id="pricing-heading" className="pricing-section__title">
                Choose your plan
              </h2>
              <p className="pricing-section__sub">
                You’re not paying for a meal plan. You’re paying for guidance that adapts to you.
              </p>
              <p className="pricing-section__sub pricing-section__sub--note">
                👉 Includes personalized adjustments based on your progress
              </p>
            </header>

            <div className="pricing-section__grid" aria-label="Pricing plans">
              <article className="card pricing-card pricing-section__card pricing-section__card--starter" aria-label="Starter plan">
                <header className="pricing-section__card-head">
                  <div className="pricing-section__plan">
                    <span className="pricing-section__plan-badge" aria-hidden>
                      Starter
                    </span>
                    <h3 className="pricing-section__plan-name">🟢 Starter Plan</h3>
                  </div>
                  <div className="pricing-section__price">
                    <span className="pricing-section__amount">₹199</span>
                    <span className="pricing-section__period">/month</span>
                  </div>
                </header>

                <ul className="pricing-section__list" aria-label="Starter includes">
                  <li>Daily meal structure</li>
                  <li>Habit guidance</li>
                  <li>Progress tracking</li>
                </ul>

                <button className="btn btn-primary wide pricing-section__btn" type="button" onClick={() => openModal('Starter Plan')}>
                  👉 Choose Starter
                </button>
              </article>

              <article className="card pricing-card pricing-section__card pricing-section__card--popular" aria-label="Founding plan">
                <div className="popular-pill pricing-section__pill">Most Popular</div>
                <header className="pricing-section__card-head">
                  <div className="pricing-section__plan">
                    <span className="pricing-section__plan-badge pricing-section__plan-badge--popular" aria-hidden>
                      Founding
                    </span>
                    <h3 className="pricing-section__plan-name">🔥 Founding Plan</h3>
                  </div>
                  <div className="pricing-section__price">
                    <span className="pricing-section__amount">₹299</span>
                    <span className="pricing-section__period">/month</span>
                  </div>
                </header>

                <div className="deal pricing-section__deal">🔥 Early Access Offer: Get 2 months for ₹299</div>

                <ul className="pricing-section__list" aria-label="Founding includes">
                  <li>Everything in Starter</li>
                  <li>AI-based adjustments</li>
                  <li>Personalized guidance</li>
                  <li>Priority features</li>
                  <li>Lifetime price lock</li>
                </ul>

                <button className="btn btn-primary wide pricing-section__btn" type="button" onClick={() => openModal('Founding Plan')}>
                  👉 Claim Founding Offer
                </button>
              </article>
            </div>
          </div>
        </section>

        <FaqSection />

        <section className="final" id="final" aria-label="Final call to action">
          <div className="final-bg" aria-hidden />
          <div className="container final-inner">
            <div className="final-card card">
              <h2>Start your journey without tracking</h2>
              <p className="section-sub">No tracking. No stress. Just results.</p>
              <button className="btn btn-primary" type="button" onClick={() => openModal('Founding Plan')}>
                👉 Lock Early Access
              </button>
              <p className="final-micro">👉 Notrack = AI-powered, no-tracking, personalized consistency system</p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
